/**
 * Void Sanctuary — base segura REAL (safe zone) do Player. É a ÚNICA fonte de verdade
 * da geometria/regra da zona: safe zone (raio) + combat buffer (histerese anti-cheese).
 *
 *   d = distância(centro, pos)
 *   d <= radius            -> "sanctuary": imune, cura, SEM combate/hook, inimigos desengajam
 *   radius < d <= combatR  -> "buffer":    NÃO imune, mas ainda SEM combate/hook (anti-borda)
 *   d > combatRadius       -> "combat":    combate normal
 *
 * A LÓGICA é compartilhada por todas as regiões; só o VISUAL é temático (applyTheme):
 *   forest -> pedras antigas + cristal roxo  ·  wilds -> cristais grandes  ·  tempest -> condutores.
 *
 * 10.9BP — HUB EM ANEL (raio 14): parede hexagonal ÚNICA, 12 pilares, núcleo = pedestal baixo com
 * anel de runas girando, CAMINHOS DE PEDRA do centro até cada estrutura (GameConfig.hub) e 4
 * lampiões emissivos entre elas (SEM PointLight extra — só a luz do núcleo). A decoração temática
 * agora é construída SÓ pro tema ativo (destrói/reconstrói no applyTheme) — antes os 3 temas
 * viviam na cena o tempo todo alternando .visible.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { randRange } from "../utils/math";
import { crystalStdMat, darkStoneMat, energyMat, stoneMat } from "./Materials";

export type SanctuaryZone = "sanctuary" | "buffer" | "combat";
export type SanctuaryVariant = "forest" | "wilds" | "tempest";

export interface SanctuaryTheme {
  variant: SanctuaryVariant;
  aura: number; // disco no chão
  ring: number; // anel de runas
  wall: number; // parede de energia
  crystal: number; // cor do núcleo
  crystalEmissive: number;
  stone: number; // pedras/decoração
  light: number; // luz pontual
}

const _seg = new THREE.Vector3();
const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _s = new THREE.Vector3(1, 1, 1);

/** 10.5K — textura procedural de malha HEXAGONAL (linhas brilhantes) p/ a parede da barreira.
 * Canvas dimensionado p/ costurar sem emenda (6 colunas × 6 linhas de hexágonos). */
function makeHexTexture(): THREE.CanvasTexture {
  const W = 250;
  const H = 216;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);
  const w = W / 6; // largura de um hexágono (pointy-top)
  const r = w / Math.sqrt(3);
  const h = 1.5 * r;
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.shadowColor = "rgba(255,255,255,0.95)";
  ctx.shadowBlur = 5;
  for (let row = -1; row <= 7; row++) {
    for (let col = -1; col <= 7; col++) {
      const cx = col * w + (row % 2 ? w / 2 : 0);
      const cy = row * h;
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = Math.PI / 6 + (k * Math.PI) / 3;
        const x = cx + Math.cos(a) * (r - 2.2);
        const y = cy + Math.sin(a) * (r - 2.2);
        if (k === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      // algumas células preenchidas de leve (leitura de "campo de energia")
      if ((row * 7 + col * 3) % 5 === 0) {
        ctx.fillStyle = "rgba(255,255,255,0.14)";
        ctx.fill();
      }
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(22, 1.3); // 10.9BP: raio 14 → mais repetições pra manter o tamanho do hexágono
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Libera as GEOMETRIAS de um grupo inteiro (usado ao trocar o tema da decoração).
 * 10.9BQ — materiais NÃO são liberados aqui: vêm da biblioteca compartilhada (Materials.ts);
 * os clones animados são liberados à parte em rebuildDecor. */
function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.geometry?.dispose();
  });
}

export class Sanctuary {
  readonly center: THREE.Vector3;
  readonly radius: number;
  /** Raio externo do buffer: só volta a lutar após sair daqui (histerese). */
  readonly combatRadius: number;

  private readonly cfg = GameConfig.sanctuary;
  private readonly group = new THREE.Group();
  /** núcleo: anel de runas girando sobre um pedestal baixo */
  private core!: THREE.Mesh;
  private crystalMat!: THREE.MeshStandardMaterial;
  private auraMat!: THREE.MeshBasicMaterial;
  private ringMat!: THREE.MeshBasicMaterial;
  // 10.5K — BARREIRA VISÍVEL no mundo: parede hexagonal animada + pilares de energia na divisa
  private hexMat!: THREE.MeshBasicMaterial;
  private hexTex!: THREE.CanvasTexture;
  private pillarMat!: THREE.MeshBasicMaterial;
  private readonly pillars: THREE.Object3D[] = [];
  private light!: THREE.PointLight;
  // 10.9BP — lampiões (só emissivo) e caminhos de pedra
  private lampMat!: THREE.MeshStandardMaterial;
  private readonly lamps: THREE.Mesh[] = [];
  // decoração do tema ATIVO (só um por vez)
  private decor: THREE.Group | null = null;
  private decorMats: THREE.MeshStandardMaterial[] = []; // 10.9BQ: clones animados (liberados no rebuild)
  private variant: SanctuaryVariant = "forest";
  private pulse = 0;

  constructor(scene: THREE.Scene) {
    this.center = new THREE.Vector3(this.cfg.center.x, 0, this.cfg.center.z);
    this.radius = this.cfg.radius;
    this.combatRadius = this.cfg.radius + this.cfg.buffer;
    const group = this.group;
    group.position.copy(this.center);

    // 10.9BQ — materiais da biblioteca; aura/anel/pilares/núcleo são CLONES porque pulsam e
    // trocam de cor com o tema (regra do Materials.ts).
    // aura no chão (disco translúcido)
    this.auraMat = energyMat(0x4a7bff, "glow").clone();
    this.auraMat.opacity = 0.16;
    const aura = new THREE.Mesh(new THREE.CircleGeometry(this.radius, 64), this.auraMat);
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.03;
    group.add(aura);

    // anel de runas na borda exata da safe zone
    this.ringMat = energyMat(0x8ea2ff, "ring").clone();
    const ring = new THREE.Mesh(new THREE.RingGeometry(this.radius - 0.45, this.radius, 128), this.ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.05;
    group.add(ring);

    // 10.5K — a DIVISA precisa ser lida no mundo (não só no minimapa): malha hexagonal
    // animada (textura procedural) subindo pela parede + pilares de energia na borda.
    // 10.9BP — parede ÚNICA (a parede lisa aditiva que ficava empilhada por baixo foi removida).
    this.hexTex = makeHexTexture();
    this.hexMat = new THREE.MeshBasicMaterial({ map: this.hexTex, color: 0x8ea2ff, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    const hexWall = new THREE.Mesh(new THREE.CylinderGeometry(this.radius + 0.05, this.radius + 0.05, 3.4, 96, 1, true), this.hexMat);
    hexWall.position.y = 1.7;
    group.add(hexWall);
    this.pillarMat = energyMat(0x9fb4ff, "crystal").clone();
    const postGeo = new THREE.BoxGeometry(0.14, 3.2, 0.14);
    const capGeo = new THREE.OctahedronGeometry(0.22, 0);
    const N_PILLARS = 12;
    for (let i = 0; i < N_PILLARS; i++) {
      const a = (i / N_PILLARS) * Math.PI * 2;
      const pillar = new THREE.Group();
      pillar.position.set(Math.cos(a) * this.radius, 0, Math.sin(a) * this.radius);
      const post = new THREE.Mesh(postGeo, this.pillarMat);
      post.position.y = 1.6;
      pillar.add(post);
      const cap = new THREE.Mesh(capGeo, this.pillarMat);
      cap.position.y = 3.35;
      pillar.add(cap);
      group.add(pillar);
      this.pillars.push(pillar);
    }
    // anel externo fino: leitura da divisa no chão mesmo com a parede fraca
    const outer = new THREE.Mesh(new THREE.RingGeometry(this.radius + 0.35, this.radius + 0.5, 128), this.ringMat);
    outer.rotation.x = -Math.PI / 2;
    outer.position.y = 0.05;
    group.add(outer);

    // 10.9BP — NÚCLEO: pedestal BAIXO (0.3) com o anel de runas girando devagar por cima. Baixo de
    // propósito: o Caçador nasce/respawna em (0,0) e a câmera de cima não pode perdê-lo (10.6A).
    const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 1.9, 0.12, 8), darkStoneMat());
    pedestal.position.y = 0.06;
    pedestal.receiveShadow = true;
    group.add(pedestal);
    this.crystalMat = crystalStdMat(0x9d7bff, 0x6a3bff, 1.1).clone();
    this.crystalMat.transparent = true;
    this.crystalMat.opacity = 0.8;
    this.crystalMat.depthWrite = false;
    this.crystalMat.side = THREE.DoubleSide;
    this.core = new THREE.Mesh(new THREE.RingGeometry(1.32, 1.6, 6), this.crystalMat);
    this.core.rotation.x = -Math.PI / 2;
    this.core.position.y = 0.14;
    group.add(this.core);

    // ÚNICA luz pontual do hub (Altar/portais não têm luz própria — só emissivo)
    this.light = new THREE.PointLight(0x7c4dff, 6, 22, 2);
    this.light.position.set(0, 2.4, 0);
    group.add(this.light);

    this.buildPaths();
    this.buildLamps();

    scene.add(group);
    this.applyTheme(GameConfig.sanctuary.themes.forest as SanctuaryTheme);
  }

  /** 10.9BP — CAMINHOS DE PEDRA: lajotas (InstancedMesh) do pedestal até cada estrutura do anel
   * (altar, correio, 2 portais). Duas fileiras por caminho, com leve desalinhamento. */
  private buildPaths(): void {
    const hub = GameConfig.hub;
    const targets = [hub.altar, hub.mailbox, hub.pvpPortal, hub.bossPortal];
    const STEP = 1.25; // avanço entre lajotas
    const START = 2.4; // sai da borda do pedestal
    const END_GAP = 2.1; // para antes da plataforma da estrutura (raio ~1.7–1.9)
    type Slab = { x: number; z: number; a: number };
    const slabs: Slab[] = [];
    for (const t of targets) {
      const dx = t.x - this.cfg.center.x;
      const dz = t.z - this.cfg.center.z;
      const len = Math.hypot(dx, dz);
      const ux = dx / len, uz = dz / len;
      const px = -uz, pz = ux; // perpendicular
      const a = Math.atan2(ux, uz); // rotação Y pra alinhar o comprimento da lajota ao caminho
      for (let d = START, k = 0; d <= len - END_GAP; d += STEP, k++) {
        const jitter = (k % 2 ? 0.08 : -0.08);
        for (const side of [-0.42, 0.42]) {
          const off = side + jitter * (side > 0 ? 1 : -1);
          slabs.push({ x: ux * d + px * off, z: uz * d + pz * off, a });
        }
      }
    }
    const geo = new THREE.BoxGeometry(0.7, 0.08, 1.1); // 1.1 ao longo do caminho (z local)
    const inst = new THREE.InstancedMesh(geo, stoneMat(0x1e1b30), slabs.length);
    for (let i = 0; i < slabs.length; i++) {
      const s = slabs[i];
      _q.setFromAxisAngle(_v.set(0, 1, 0), s.a + (i % 3 - 1) * 0.04);
      _m4.compose(_v.set(s.x, 0.045, s.z), _q, _s);
      inst.setMatrixAt(i, _m4);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.receiveShadow = true;
    this.group.add(inst);
  }

  /** 10.9BP — 4 lampiões pequenos (poste fino + octaedro emissivo) nos vãos ENTRE estruturas.
   * Sem PointLight: só emissivo (o hub já tem a luz do núcleo). */
  private buildLamps(): void {
    const r = GameConfig.hub.ringRadius;
    // ângulos entre altar(N) / correio(O) / PvP(SO) / Fenda(SE) — o sul fica livre pro TestPad
    const angles = [0, -Math.PI / 4, -Math.PI * 0.75, Math.PI * 0.875];
    const postMat = darkStoneMat();
    const postGeo = new THREE.CylinderGeometry(0.07, 0.1, 2.0, 6);
    this.lampMat = crystalStdMat(0xffd1a0, 0xffa040, 1.4).clone(); // tremula → clone
    const lampGeo = new THREE.OctahedronGeometry(0.2, 0);
    for (const a of angles) {
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      const post = new THREE.Mesh(postGeo, postMat);
      post.position.set(x, 1.0, z);
      post.castShadow = true;
      this.group.add(post);
      const lamp = new THREE.Mesh(lampGeo, this.lampMat);
      lamp.position.set(x, 2.2, z);
      this.group.add(lamp);
      this.lamps.push(lamp);
    }
  }

  // ---------------------------------------------------------------- decoração temática
  private buildForestDecor(g: THREE.Group): void {
    const stoneGeo = new THREE.DodecahedronGeometry(0.7, 0);
    const rock = stoneMat(0x3a3550);
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2 + 0.13;
      const s = new THREE.Mesh(stoneGeo, rock);
      const h = 0.8 + (i % 3) * 0.3;
      s.scale.set(1, h, 1);
      s.position.set(Math.cos(a) * (this.radius - 0.7), h * 0.6, Math.sin(a) * (this.radius - 0.7));
      s.rotation.y = a;
      s.castShadow = true;
      g.add(s);
    }
  }

  private buildWildsDecor(g: THREE.Group): void {
    const rock = stoneMat(0x50627d);
    const crystalGeo = new THREE.OctahedronGeometry(1, 0);
    // 10.9BQ — UM clone por tipo de cristal (todos pulsam juntos), não um material por mesh
    const cm = crystalStdMat(0x62e8ff).clone();
    this.decorMats.push(cm);
    // 8 cristais grandes integrados ao círculo (na borda)
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.3;
      const c = new THREE.Mesh(crystalGeo, cm);
      const s = randRange(1.1, 1.8);
      c.position.set(Math.cos(a) * (this.radius - 0.9), s * 0.7, Math.sin(a) * (this.radius - 0.9));
      c.scale.set(s * 0.5, s, s * 0.5);
      c.rotation.y = randRange(0, 6);
      c.castShadow = true;
      g.add(c);
      const base = new THREE.Mesh(new THREE.DodecahedronGeometry(0.6, 0), rock);
      base.position.set(c.position.x, 0.3, c.position.z);
      base.scale.setScalar(randRange(0.8, 1.3));
      base.castShadow = true;
      g.add(base);
    }
    // shards pequenos quebrados — só na faixa externa (fora do anel das estruturas/caminhos)
    const sm = crystalStdMat(0x8ea2ff, 0x8ea2ff, 0.7).clone();
    this.decorMats.push(sm);
    for (let i = 0; i < 12; i++) {
      const a = randRange(0, Math.PI * 2);
      const r = randRange(GameConfig.hub.ringRadius + 2.2, this.radius - 1.2);
      const shard = new THREE.Mesh(crystalGeo, sm);
      const s = randRange(0.2, 0.5);
      shard.position.set(Math.cos(a) * r, s, Math.sin(a) * r);
      shard.scale.set(s * 0.5, s * 1.6, s * 0.5);
      shard.rotation.set(randRange(-0.3, 0.3), randRange(0, 6), randRange(-0.3, 0.3));
      g.add(shard);
    }
    // terreno rachado convergindo para o núcleo (linhas emissivas)
    const crackMat = energyMat(0x62e8ff, "glow");
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const crack = new THREE.Mesh(new THREE.PlaneGeometry(0.25, this.radius - 1), crackMat);
      crack.rotation.x = -Math.PI / 2;
      crack.position.set(Math.cos(a) * (this.radius / 2), 0.04, Math.sin(a) * (this.radius / 2));
      crack.rotation.z = -a;
      g.add(crack);
    }
  }

  /** Decoração TEMPEST (Fase 10): círculo de pedra clara + cristais condutores ALTOS
   * (cones finos) com arcos de energia entre eles e runas no chão. Identidade própria. */
  private buildTempestDecor(g: THREE.Group): void {
    const rock = stoneMat(0x8496ad);
    const coneGeo = new THREE.ConeGeometry(0.35, 2.6, 5);
    const tips: THREE.Vector3[] = [];
    const cm = crystalStdMat(0xbfefff, 0x7fd6ff).clone(); // pulsa → clone único
    this.decorMats.push(cm);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 0.25;
      const c = new THREE.Mesh(coneGeo, cm);
      const s = randRange(1.0, 1.5);
      const px = Math.cos(a) * (this.radius - 0.9);
      const pz = Math.sin(a) * (this.radius - 0.9);
      c.position.set(px, s * 1.3, pz);
      c.scale.set(s * 0.7, s, s * 0.7);
      c.castShadow = true;
      g.add(c);
      tips.push(new THREE.Vector3(px, s * 2.6, pz));
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.85, 0.4, 6), rock);
      base.position.set(px, 0.2, pz);
      g.add(base);
    }
    // arcos de energia entre cristais adjacentes (flicker no update)
    const arcMat = energyMat(0x7fd6ff, "disc");
    for (let i = 0; i < tips.length; i++) {
      const a = tips[i], b = tips[(i + 1) % tips.length];
      const d = a.distanceTo(b);
      const arc = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, d, 4), arcMat);
      arc.position.copy(a.clone().lerp(b, 0.5));
      arc.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
      g.add(arc);
    }
    // runas no chão (anéis concêntricos discretos)
    const runeMat = energyMat(0x7fd6ff, "ring");
    for (let i = 0; i < 2; i++) {
      const rr = this.radius * (0.4 + i * 0.28);
      const rune = new THREE.Mesh(new THREE.RingGeometry(rr - 0.08, rr, 64), runeMat);
      rune.rotation.x = -Math.PI / 2;
      rune.position.y = 0.05;
      g.add(rune);
    }
  }

  /** 10.9BP — (re)constrói SÓ a decoração do tema pedido; a anterior é destruída. */
  private rebuildDecor(variant: SanctuaryVariant): void {
    if (this.decor) {
      this.group.remove(this.decor);
      disposeGroup(this.decor);
      this.decor = null;
    }
    for (const m of this.decorMats) m.dispose(); // só os CLONES animados
    this.decorMats = [];
    const g = new THREE.Group();
    if (variant === "forest") this.buildForestDecor(g);
    else if (variant === "wilds") this.buildWildsDecor(g);
    else this.buildTempestDecor(g);
    this.decor = g;
    this.group.add(g);
  }

  /** Aplica o tema visual (variant + cores). A LÓGICA da zona não muda. */
  applyTheme(theme: SanctuaryTheme): void {
    const changed = this.variant !== theme.variant || !this.decor;
    this.variant = theme.variant;
    this.auraMat.color.setHex(theme.aura);
    this.ringMat.color.setHex(theme.ring);
    this.hexMat.color.setHex(theme.wall);
    this.pillarMat.color.setHex(theme.ring);
    this.crystalMat.color.setHex(theme.crystal);
    this.crystalMat.emissive.setHex(theme.crystalEmissive);
    this.light.color.setHex(theme.light);
    if (changed) this.rebuildDecor(theme.variant);
    // núcleo (runa): wilds/tempest = marca um pouco maior
    this.core.scale.setScalar(theme.variant === "forest" ? 1 : 1.15);
  }

  /** Conveniência: aplica o tema de uma região por id. */
  applyRegionTheme(regionId: string): void {
    const themes = GameConfig.sanctuary.themes;
    const t = regionId === "shatteredWilds" ? themes.wilds : regionId === "tempestReach" ? themes.tempest : themes.forest;
    this.applyTheme(t as SanctuaryTheme);
  }

  /** O ponto (x,z) está dentro da SAFE ZONE (imune/cura)? */
  contains(pos: { x: number; z: number }): boolean {
    return this.distanceSq(pos) <= this.radius * this.radius;
  }
  isInside(pos: { x: number; z: number }): boolean {
    return this.contains(pos);
  }
  containsXZ(x: number, z: number): boolean {
    const dx = x - this.center.x;
    const dz = z - this.center.z;
    return dx * dx + dz * dz <= this.radius * this.radius;
  }
  inNoCombatArea(pos: { x: number; z: number }): boolean {
    return this.distanceSq(pos) <= this.combatRadius * this.combatRadius;
  }
  zoneOf(pos: { x: number; z: number }): SanctuaryZone {
    const d2 = this.distanceSq(pos);
    if (d2 <= this.radius * this.radius) return "sanctuary";
    if (d2 <= this.combatRadius * this.combatRadius) return "buffer";
    return "combat";
  }
  distanceFromBoundary(pos: { x: number; z: number }): number {
    return Math.sqrt(this.distanceSq(pos)) - this.radius;
  }
  private distanceSq(pos: { x: number; z: number }): number {
    const dx = pos.x - this.center.x;
    const dz = pos.z - this.center.z;
    return dx * dx + dz * dz;
  }

  keepOut(pos: THREE.Vector3, margin = 0): void {
    const dx = pos.x - this.center.x;
    const dz = pos.z - this.center.z;
    const r = this.radius + margin;
    const d = Math.hypot(dx, dz);
    if (d < r && d > 1e-4) {
      pos.x = this.center.x + (dx / d) * r;
      pos.z = this.center.z + (dz / d) * r;
    }
  }

  projectileCrossing(from: THREE.Vector3, to: THREE.Vector3, out: THREE.Vector3): boolean {
    const toInside = this.contains(to);
    if (!toInside) return false;
    const fromInside = this.contains(from);
    if (fromInside) {
      out.copy(from);
      return true;
    }
    const ox = from.x - this.center.x;
    const oz = from.z - this.center.z;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const a = dx * dx + dz * dz;
    if (a < 1e-9) {
      out.copy(to);
      return true;
    }
    const b = 2 * (ox * dx + oz * dz);
    const c = ox * ox + oz * oz - this.radius * this.radius;
    const disc = b * b - 4 * a * c;
    if (disc < 0) {
      out.copy(to);
      return true;
    }
    const t = Math.max(0, (-b - Math.sqrt(disc)) / (2 * a));
    _seg.set(dx, to.y - from.y, dz).multiplyScalar(t);
    out.copy(from).add(_seg);
    return true;
  }

  update(dt: number): void {
    this.pulse += dt;
    this.core.rotation.z -= dt * 0.25; // 10.9BP: gira devagar sobre o pedestal
    this.crystalMat.emissiveIntensity = 1 + Math.sin(this.pulse * 1.5) * 0.35;
    this.auraMat.opacity = 0.12 + Math.sin(this.pulse * 1.2) * 0.05;
    this.ringMat.opacity = 0.55 + Math.sin(this.pulse * 1.6) * 0.18;
    // 10.5K: malha hexagonal sobe devagar + respira; pilares pulsam em onda
    this.hexTex.offset.y -= dt * 0.06;
    this.hexTex.offset.x += dt * 0.012;
    this.hexMat.opacity = 0.58 + Math.sin(this.pulse * 1.3) * 0.14;
    for (let i = 0; i < this.pillars.length; i++) {
      const p = this.pillars[i];
      const s = 1 + Math.sin(this.pulse * 2.2 + i * 0.7) * 0.06;
      p.scale.set(s, 1, s);
      p.children[1].rotation.y += dt * 1.2;
    }
    // lampiões: tremulação suave da chama
    this.lampMat.emissiveIntensity = 1.3 + Math.sin(this.pulse * 5.1) * 0.15 + Math.sin(this.pulse * 2.3) * 0.1;
    for (let i = 0; i < this.lamps.length; i++) this.lamps[i].rotation.y += dt * (0.6 + i * 0.1);
    // só o tema VISÍVEL anima (os outros nem existem na cena)
    if (this.decorMats.length && this.variant !== "forest") {
      const e = 0.7 + Math.sin(this.pulse * (this.variant === "wilds" ? 2 : 2.2)) * 0.3;
      for (const m of this.decorMats) m.emissiveIntensity = e;
    }
  }
}
